import { QueryClient, useQuery } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import localeData from "dayjs/plugin/localeData";
import { useEffect } from "react";

import { platformApi } from "@/app/platform";
import { DefaultErrorComponent } from "@/components/Errors/DefaultErrorComponent";
import { UnderMaintenance } from "@/components/UnderMaintenance/UnderMaintenance";
import { PaymentContextProvider } from "@/contexts/PaymentContext";
import { SnackbarProvider } from "@/contexts/SnackbarContext";
import type { HotelBookingStep } from "@/features/hotels/types/types";
import { applicationStateQueryOptions } from "@/hooks/useApplicationStateTurnedOff";

dayjs.extend(durationPlugin);
dayjs.extend(localeData);

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    hotelStep?: HotelBookingStep;
    drawer?: boolean;
  }
}

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RouteComponent,
  errorComponent: DefaultErrorComponent,
});

function RouteComponent() {
  const router = useRouterState();

  const { data: underMaintenance } = useQuery(applicationStateQueryOptions());

  useEffect(() => {
    platformApi.setNavigationBar(
      router.location.pathname === "/"
        ? {
            backgroundColor: "#003E2B",
            image: `${location.origin}/tzlogo.png`,
          }
        : {
            backgroundColor: "#f5f6f5",
            image: null,
          },
    );
  }, [router.location.pathname]);

  return (
    <SnackbarProvider>
      {underMaintenance ? (
        <main className="h-dvh w-dvh">
          <UnderMaintenance />
        </main>
      ) : (
        <PaymentContextProvider>
          <Outlet />
        </PaymentContextProvider>
      )}
    </SnackbarProvider>
  );
}
